/**
 * Tracking Service - Simple transport-based event tracking
 *
 * Supports: Mixpanel, Customer.io, Console
 *
 * Usage:
 *   import { trackEvent } from '@services/tracking';
 *   trackEvent({ type: 'Sign Up', payload: { ... } });
 */

import auth, { isProduction } from '@client/components/auth';
import { init as coreInit, track as coreTrack } from './tracking.core';
import { EventName, EventPayloadsMap, TrackingPayload } from './tracking.types';
import { MixPanelEvent } from '../mixpanel/events';

// Re-export types
export type { EventName } from './tracking.types';
export * from '../mixpanel/events';

// Auto-initialization
let _initialized = false;

const ensureInitialized = (): void => {
  if (_initialized) return;
  coreInit();
  _initialized = true;
};

/**
 * Get user context for tracking.
 */
const getUserContext = () => {
  const user = auth.getUser();
  if (!user) return null;

  return {
    uid: user.id,
    influencerId: user?.influencer?.id,
    influencerName: user.influencer
      ? `${user.influencer.firstName} ${user.influencer.lastName}`
      : undefined,
  };
};

/**
 * Track an event (backward-compatible API).
 * Auto-injects user context. Only tracks for influencers.
 */
export const trackEvent = <T extends EventName>(
  event: {
    type: T;
    payload?: Omit<EventPayloadsMap[T], 'Influencer ID' | 'Influencer Name'>;
  },
  _props?: { optionsOrCb?: unknown; cb?: unknown },
): void => {
  if (!isProduction()) {
    console.log('[trackEvent]:', event.type, event.payload);
  }

  // Only track for influencers
  if (!auth.isInfluencer()) return;

  ensureInitialized();

  const userContext = getUserContext();
  const payload: TrackingPayload = {
    ...event.payload,
    uid: userContext?.uid ?? '',
    ...(userContext && {
      'Influencer ID': userContext.influencerId,
      'Influencer Name': userContext.influencerName,
    }),
  };

  coreTrack(event.type, payload);
};

/**
 * Track login event (backward-compatible API).
 */
export const trackLogin = (
  payload: { uid: string } & Partial<EventPayloadsMap['Log In']>,
): void => {
  ensureInitialized();
  coreTrack('Log In', payload as TrackingPayload);
};

/**
 * Low-level track function with strongly typed events.
 * Bypasses influencer check. Requires uid in payload.
 */
export const track = <T extends MixPanelEvent>(
  event: T & { payload: { uid: string | number } },
  _props?: { optionsOrCb?: unknown; cb?: unknown },
): void => {
  if (!isProduction()) {
    console.log('[track]:', event.type, event.payload);
  }

  ensureInitialized();
  coreTrack(event.type, event.payload as TrackingPayload);
};
